import { Group, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'

const dragTooltipStyles = {
  tooltip: {
    textAlign: 'center' as const,
    '& em': {
      fontStyle: 'normal',
      color: '#868e96',
      display: 'inline-block',
    },
  },
}

interface OptionPillProps {
  subtopic?: {
    id: string
    key: string
    displayName: string
  }

  option?: string

  isAddButton?: boolean
  onAdd?: () => void
  onDelete?: (id?: string) => void
  onEdit?: (newValue: string) => void
  canEdit?: boolean
  addTooltip?: string
  fontSize?: string
  shouldFocus?: boolean
}

export function OptionPill({
  subtopic,
  option,
  isAddButton,
  onAdd,
  onDelete,
  onEdit,
  canEdit = false,
  addTooltip,
  fontSize = '14px',
  shouldFocus = false,
}: OptionPillProps) {
  const [showDelete, setShowDelete] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(option || subtopic?.displayName || '')
  const inputRef = useRef<HTMLInputElement>(null)
  const [addTooltipOpened, setAddTooltipOpened] = useState(false)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Handle auto-focus for new subtopics
  useEffect(() => {
    if (shouldFocus) {
      setIsEditing(true)
    }
  }, [shouldFocus])

  const handleEdit = () => {
    if (editValue.trim() && editValue !== (option || subtopic?.displayName)) {
      onEdit?.(editValue)
    } else {
      setEditValue(option || subtopic?.displayName || '') // Reset to original value if empty or unchanged
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEdit()
    } else if (e.key === 'Escape') {
      setEditValue(option || subtopic?.displayName || '')
      setIsEditing(false)
    }
  }

  if (isAddButton) {
    if (!canEdit) return null // Don't show add button if not editable
    return (
      <Tooltip label={addTooltip} position="top" styles={dragTooltipStyles} opened={addTooltipOpened}>
        <Group
          gap={4}
          style={{
            borderRadius: '16px',
            padding: '4px 12px',
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setAddTooltipOpened(true)}
          onMouseLeave={() => setAddTooltipOpened(false)}
          onClick={e => {
            e.stopPropagation()
            setAddTooltipOpened(false)
            onAdd?.()
          }}>
          <IconPlus size={14} />
        </Group>
      </Tooltip>
    )
  }

  return (
    <Group
      gap={4}
      style={{
        backgroundColor: 'var(--mantine-color-blue-0)',
        borderRadius: '16px',
        padding: '4px 12px',
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        paddingRight: canEdit ? '30px' : '12px',
        flexGrow: 1,
        transition: 'padding 150ms ease',
        ':hover': {
          paddingRight: canEdit ? '32px' : '12px', // Expand padding on hover
        },
      }}
      onMouseEnter={() => canEdit && setShowDelete(true)}
      onMouseLeave={() => canEdit && setShowDelete(false)}>
      {isEditing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleEdit}
          onKeyDown={handleKeyDown}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0,
            fontSize: fontSize,
            fontFamily: 'inherit',
            width: '100%',
            maxWidth: '300px',
            minWidth: '2ch',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        />
      ) : (
        <Text
          size="sm"
          style={{
            cursor: canEdit ? 'text' : 'default',
            fontSize: fontSize,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
          onClick={() => canEdit && setIsEditing(true)}>
          {option || subtopic?.displayName}
        </Text>
      )}
      {canEdit && (
        <UnstyledButton
          onClick={() => onDelete?.(subtopic?.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px',
            borderRadius: '50%',
            transition: 'opacity 150ms ease',
            opacity: showDelete && !isEditing ? 1 : 0,
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'var(--mantine-color-blue-0)',
            width: '24px',
            height: '24px',
          }}>
          <IconX size={14} style={{ paddingLeft: '2px', borderLeft: '1px solid black' }} />
        </UnstyledButton>
      )}
    </Group>
  )
}
